import ReactGA from 'react-ga4';

/**
 * Analytics service for Google Analytics 4 integration
 * Provides centralized analytics tracking for the application
 */
class AnalyticsService {
  constructor() {
    this.isInitialized = false;
    this.measurementId = null;
  }

  /**
   * Initialize Google Analytics 4
   * @param {string} measurementId - GA4 Measurement ID (format: G-XXXXXXXXXX)
   */
  initialize(measurementId) {
    if (this.isInitialized) {
      console.warn('Analytics already initialized');
      return;
    }

    if (!measurementId) {
      console.error('Measurement ID is required for analytics initialization');
      return;
    }

    this.measurementId = measurementId;
    
    try {
      ReactGA.initialize(measurementId, {
        testMode: import.meta.env.MODE === 'development',
      });
      
      this.isInitialized = true;
      console.log('Google Analytics 4 initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Google Analytics:', error);
    }
  }

  /**
   * Track page view
   * @param {string} page - Page path
   * @param {string} title - Page title
   */
  trackPageView(page = window.location.pathname, title = document.title) {
    if (!this.isInitialized) {
      console.warn('Analytics not initialized. Cannot track page view.');
      return;
    }

    try {
      ReactGA.send({ 
        hitType: 'pageview', 
        page, 
        title 
      });
    } catch (error) {
      console.error('Failed to track page view:', error);
    }
  }

  /**
   * Track custom event with GA4 parameters
   * @param {Object} eventData - Event data object
   * @param {string} eventData.category - Event category
   * @param {string} eventData.action - Event action
   * @param {string} [eventData.label] - Event label
   * @param {number} [eventData.value] - Event value
   * @param {Object} [eventData.params] - Additional GA4 custom parameters
   */
  trackEvent({ category, action, label, value, params = {} }) {
    if (!this.isInitialized) {
      console.warn('Analytics not initialized. Cannot track event.');
      return;
    }

    try {
      // GA4 uses a flatter event structure with custom parameters
      ReactGA.event({
        category,
        action,
        label,
        value,
        ...params // Spread custom parameters for GA4 custom dimensions
      });
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  }

  /**
   * Track open house view with detailed property data
   * @param {string} propertyId - Property identifier (MLS #)
   * @param {string} propertyAddress - Property address
   * @param {Object} [propertyData] - Additional property data
   */
  trackOpenHouseView(propertyId, propertyAddress, propertyData = {}) {
    this.trackEvent({
      category: 'Open House',
      action: 'View',
      label: propertyAddress,
      value: 1,
      params: {
        property_id: propertyId,
        property_address: propertyAddress,
        listing_agent: propertyData.agentName || 'Unknown',
        property_price: propertyData.price || 0,
        property_city: propertyData.city || 'Unknown',
        property_neighborhood: propertyData.neighborhood || 'Unknown',
        bedrooms: propertyData.beds || 0,
        bathrooms: propertyData.baths || 0,
        square_feet: propertyData.sqft || 0,
      }
    });
  }

  /**
   * Track contact action with agent details
   * @param {string} method - Contact method (phone, email, etc.)
   * @param {string} agentName - Agent name or identifier
   * @param {Object} [contactData] - Additional contact context
   */
  trackContact(method, agentName, contactData = {}) {
    this.trackEvent({
      category: 'Contact',
      action: method,
      label: agentName,
      value: 1,
      params: {
        contact_method: method,
        agent_name: agentName,
        contact_source: contactData.source || 'Unknown', // e.g., 'Header', 'Modal'
        property_context: contactData.propertyId || 'General'
      }
    });
  }

  /**
   * Track map interaction with property context
   * @param {string} action - Map action (marker_click, photo_open, etc.)
   * @param {Object} [data] - Property and interaction data
   */
  trackMapInteraction(action, data = {}) {
    this.trackEvent({
      category: 'Map',
      action,
      label: data.address || data.propertyId || 'unknown',
      value: 1,
      params: {
        property_id: data.propertyId || 'Unknown',
        property_address: data.address || 'Unknown',
        interaction_type: action
      }
    });
  }

  /**
   * Track filter usage
   * @param {string} filterType - Type of filter used
   * @param {string} filterValue - Filter value
   * @param {string} [viewContext] - Optional view context (Gallery, Map)
   */
  trackFilter(filterType, filterValue, viewContext) {
    this.trackEvent({
      category: 'Filter',
      action: filterType,
      label: filterValue,
      value: 1,
      params: viewContext ? { view_context: viewContext } : {}
    });
  }

  /**
   * Get UTM parameters from URL for traffic source tracking
   * GA4 automatically captures these, but this can be useful for custom tracking
   * @returns {Object} - UTM parameters object
   */
  getUtmParams() {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      utm_source: urlParams.get('utm_source') || '',
      utm_medium: urlParams.get('utm_medium') || '',
      utm_campaign: urlParams.get('utm_campaign') || '',
      utm_term: urlParams.get('utm_term') || '',
      utm_content: urlParams.get('utm_content') || ''
    };
  }

  /**
   * Get traffic source information
   * @returns {Object} - Traffic source details
   */
  getTrafficSource() {
    return {
      referrer: document.referrer || 'direct',
      landing_page: window.location.pathname,
      ...this.getUtmParams()
    };
  }
}

// Export singleton instance
export default new AnalyticsService();

